import React, { useEffect, useRef, useState } from "react";
import { X, Send, Sparkles, Loader2, Zap, Wrench, ChevronRight } from "lucide-react";
import apiClient, { formatApiError } from "@/api/client";

const QUICK_PROMPTS = [
  { label: "Bugün ne yapmalıyım?", prompt: "Bugün en acil ilgilenmem gereken 3 şeyi sırala ve neden olduğunu açıkla." },
  { label: "Haftalık brifing", prompt: "Bu hafta için detaylı bir çalışma planı hazırla." },
  { label: "Gecikmiş görevler", prompt: "Süresi geçmiş veya yaklaşan tüm görev ve domain uyarılarını listele." },
  { label: "Yeni görev oluştur", prompt: "Lütfen 'Yarın sabah elektronikmagaza.com.tr için domain yenileme' adında kritik öncelikli yeni bir görev oluştur." },
];

export default function AIAssistantPanel({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && !loaded) {
      apiClient.get("/ai/history").then(({ data }) => {
        const flat = [];
        data.forEach((m) => {
          flat.push({ role: "user", text: m.user_message });
          flat.push({ role: "ai", text: m.ai_response, tools: m.tools || [] });
        });
        setMessages(flat);
        setLoaded(true);
      }).catch(() => setLoaded(true));
    }
  }, [open, loaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await apiClient.post("/ai/chat", { message: msg });
      setMessages((m) => [...m, { role: "ai", text: data.response, tools: data.tools || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: `Hata: ${formatApiError(e)}`, tools: [] }]);
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ai-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full sm:w-[460px] ky-ai-surface flex flex-col ky-slide-in">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] flex items-center justify-center"><Sparkles size={16} /></div>
            <div>
              <div className="text-[10px] tracking-[0.16em] uppercase text-white/50 font-semibold">Co-Pilot</div>
              <div className="font-semibold text-[15px] leading-none mt-0.5">Akıllı Asistan</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center" data-testid="ai-close"><X size={14} /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-4">
              <div className="text-sm text-white/70 leading-relaxed">
                Merhaba. Ben CRM Co-Pilot. Verilerinizi okuyup öncelik öneriyorum; ayrıca sizin için görev oluşturma, müşteri arama, taslak yazma gibi işlemleri de yapabilirim.
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 font-semibold">Hızlı Başlangıç</div>
                <div className="space-y-2">
                  {QUICK_PROMPTS.map((q) => (
                    <button key={q.label} onClick={() => send(q.prompt)} className="group w-full text-left px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm hover:bg-white/[0.08] hover:border-white/20 transition flex items-center gap-2" data-testid={`quick-${q.label}`}>
                      <Zap size={12} className="text-[#A5B4FC]" />
                      <span className="flex-1">{q.label}</span>
                      <ChevronRight size={12} className="text-white/30 group-hover:text-white/60" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}-${i}`}>
              <div className={`max-w-[90%] ${m.role === "user" ? "bg-[#4F46E5] text-white" : "bg-white/[0.06] text-white border border-white/10"} px-4 py-2.5 rounded-xl text-[13.5px] whitespace-pre-wrap leading-relaxed`}>
                {m.text}
                {m.tools && m.tools.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
                    {m.tools.map((t, ti) => (
                      <div key={ti} className="flex items-start gap-2 text-[12px]">
                        <Wrench size={11} className="text-[#10B981] mt-0.5 shrink-0" />
                        <div>
                          <div className="font-mono font-semibold text-[#A7F3D0]">✓ {t.tool}</div>
                          <div className="text-white/70 mt-0.5 leading-relaxed">{t.result}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start" data-testid="ai-loading">
              <div className="bg-white/[0.06] border border-white/10 px-4 py-2.5 rounded-xl text-sm inline-flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                <span className="text-white/70">Düşünüyor…</span>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-white/10 p-3">
          <div className="flex gap-2 items-end">
            <textarea
              data-testid="ai-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Asistan'a sorun veya komut verin… (Enter ile gönder)"
              className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-[13.5px] placeholder-white/30 focus:outline-none focus:border-[#7C3AED] resize-none max-h-24"
            />
            <button onClick={() => send()} disabled={loading || !input.trim()} className="h-9 px-3 rounded-lg bg-[#4F46E5] disabled:opacity-40 hover:bg-[#6366F1]" data-testid="ai-send">
              <Send size={14} />
            </button>
          </div>
          <div className="text-[10px] text-white/40 mt-2 font-mono flex items-center gap-1">
            <span>Claude Sonnet 4.5</span><span>·</span><span>Tool-use aktif</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
